import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { 
  fetchOrderById, 
  listOrderMembers, 
  OrderRecord, 
  OrderMemberRecord, 
  subscribeOrderMembers, 
  subscribeOrdersForDate,
  joinOrder, 
  leaveOrder, 
  updateOrderMemberItem,
  updateOrder,
  deleteOrder,
  getFormattedTimeRemaining,
  isOrderExpired
} from "../services/lunchBuddy";
import ConfirmModal from "../components/ConfirmModal";

interface Props {
  userEmail: string;
  displayName?: string;
}

export default function OrderDetailPage({ userEmail, displayName }: Props) {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderRecord | null>(null);
  const [members, setMembers] = useState<OrderMemberRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joiningOrder, setJoiningOrder] = useState(false);
  const [leavingOrder, setLeavingOrder] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editFormData, setEditFormData] = useState({
    restaurant: '',
    deadline: 60,
    link: '',
    notes: ''
  });
  
  const dishInputRef = useRef<HTMLInputElement>(null);
  
  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    isLoading: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    isLoading: false
  });

  useEffect(() => {
    if (!orderId) {
      setError("ID order không hợp lệ");
      setLoading(false);
      return;
    }

    const loadOrderData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load order details
        const orderData = await fetchOrderById(orderId);
        if (!orderData) {
          setError("Không tìm thấy order");
          setLoading(false);
          return;
        }

        setOrder(orderData);

        // Load members
        const memberList = await listOrderMembers(orderId);
        setMembers(memberList);

      } catch (err) {
        console.error('Error loading order data:', err);
        setError("Có lỗi xảy ra khi tải dữ liệu order");
      } finally {
        setLoading(false);
      }
    };

    loadOrderData();
  }, [orderId]);

  // Subscribe to realtime updates
  useEffect(() => {
    if (!orderId || !order) return;

    // Subscribe to order changes
    const unsubscribeOrder = subscribeOrdersForDate(order.date, (payload) => {
      if (payload.record.id === orderId) {
        if (payload.action === 'update') {
          setOrder(payload.record);
        } else if (payload.action === 'delete') {
          toast.error('Order đã bị xóa');
          navigate('/orders');
        }
      }
    });

    // Subscribe to member changes
    const unsubscribeMembers = subscribeOrderMembers(orderId, (payload) => {
      if (payload.action === 'create') {
        setMembers(prev => {
          const exists = prev.some(m => m.id === payload.record.id);
          return exists ? prev : [...prev, payload.record];
        });
      } else if (payload.action === 'delete') {
        setMembers(prev => prev.filter(m => m.id !== payload.record.id));
      } else if (payload.action === 'update') {
        setMembers(prev => prev.map(m => 
          m.id === payload.record.id ? payload.record : m
        ));
      }
    });

    return () => {
      unsubscribeOrder();
      unsubscribeMembers();
    };
  }, [orderId, order, navigate]);

  const handleJoinOrder = async (itemName?: string) => {
    if (!order) return;
    
    setJoiningOrder(true);
    try {
      await joinOrder(order.id, userEmail, itemName, displayName);
      toast.success(itemName ? 'Đã tham gia và lưu món của bạn' : 'Đã tham gia order');
      
      // Reload members
      const memberList = await listOrderMembers(order.id);
      setMembers(memberList);
      
    } catch (error) {
      console.error('Error joining order:', error);
      toast.error('Có lỗi xảy ra khi tham gia order');
    } finally {
      setJoiningOrder(false);
    }
  };

  const handleLeaveOrder = async () => {
    if (!order) return;
    
    setLeavingOrder(true);
    try {
      await leaveOrder(order.id, userEmail);
      toast.success('Đã rời order');
      
      // Reload members
      const memberList = await listOrderMembers(order.id);
      setMembers(memberList);
      
    } catch (error) {
      console.error('Error leaving order:', error);
      toast.error('Có lỗi xảy ra khi rời order');
    } finally {
      setLeavingOrder(false);
    }
  };

  const handleUpdateDish = async () => {
    if (!order) return;
    
    const value = dishInputRef.current?.value?.trim() || null;
    const isUserMember = members.some(m => m.userEmail === userEmail);
    
    try {
      if (isUserMember) {
        await updateOrderMemberItem(order.id, userEmail, value);
      } else {
        await joinOrder(order.id, userEmail, value || undefined, displayName);
      }
      
      const memberList = await listOrderMembers(order.id);
      setMembers(memberList);
      toast.success(value ? 'Đã lưu món của bạn' : (isUserMember ? 'Đã xóa món' : 'Đã tham gia order'));
      
    } catch (error) {
      console.error('Error updating dish:', error);
      toast.error('Không cập nhật được tên món');
    }
  };

  const handleEditClick = () => {
    if (!order) return;
    setEditFormData({
      restaurant: order.restaurant,
      deadline: order.deadline,
      link: order.link || '',
      notes: order.notes || ''
    });
    setIsEditing(true);
  };

  const handleUpdateOrder = async () => {
    if (!order) return;
    
    setIsUpdating(true);
    try {
      await updateOrder({
        id: order.id,
        restaurant: editFormData.restaurant,
        deadline: editFormData.deadline,
        link: editFormData.link.trim() || undefined,
        notes: editFormData.notes.trim()
      });
      setIsEditing(false);
      toast.success('Đã cập nhật order');
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Có lỗi xảy ra khi cập nhật order');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!order) return;
    
    setConfirmModal({
      isOpen: true,
      title: 'Xóa Order',
      message: `Bạn có chắc muốn xóa order tại ${order.restaurant}? Hành động này không thể hoàn tác.`,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        setIsDeleting(true);
        try {
          await deleteOrder(order.id);
          toast.success('Đã xóa order');
          navigate('/orders');
        } catch (error) {
          console.error('Error deleting order:', error);
          toast.error('Có lỗi xảy ra khi xóa order');
        } finally {
          setIsDeleting(false);
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      },
      isLoading: false
    });
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Order tại ${order?.restaurant}`,
          text: `Tham gia order tại ${order?.restaurant}`,
          url: shareUrl
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Fallback to copy to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Đã copy link vào clipboard');
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        toast.error('Không thể copy link');
      }
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-white text-xl">⏳ Đang tải order...</div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="text-6xl mb-4">😞</div>
          <h2 className="text-2xl font-bold text-white mb-2">Không tìm thấy order</h2>
          <p className="text-slate-400 mb-6">{error || "Order không tồn tại hoặc đã bị xóa"}</p>
          <button
            onClick={() => navigate('/orders')}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-medium transition-all"
          >
            ← Quay lại danh sách orders
          </button>
        </div>
      </div>
    );
  }

  const isUserMember = members.some(m => m.userEmail === userEmail);
  const canEdit = order.createdBy === userEmail;
  const userMember = members.find(m => m.userEmail === userEmail);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/orders')}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 transition-all"
          >
            <span>←</span>
            <span>Quay lại</span>
          </button>
          
          <button
            onClick={handleShare}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600 text-white font-medium transition-all"
          >
            <span>🔗</span>
            <span>Chia sẻ</span>
          </button>
        </div>

        {/* Order Details */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-600/50 rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30 flex items-center justify-center">
                <span className="text-2xl">🏪</span>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">{order.restaurant}</h1>
                <p className="text-slate-400">
                  Tạo bởi {order.createdBy === userEmail ? 'Bạn' : (order.author || order.createdBy.split('@')[0])}
                </p>
                <div className="flex items-center gap-4 mt-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    order.status === 'open' ? 'bg-green-500/20 text-green-300' :
                    order.status === 'closed' ? 'bg-red-500/20 text-red-300' :
                    'bg-yellow-500/20 text-yellow-300'
                  }`}>
                    {order.status === 'open' ? '🟢 Đang mở' :
                     order.status === 'closed' ? '🔴 Đã đóng' :
                     '🟡 Đang xử lý'}
                  </span>
                  <span className={`font-medium ${isOrderExpired(order) ? 'text-red-400' : 'text-orange-400'}`}>
                    {isOrderExpired(order) ? '⏰ Đã hết hạn' : `⏰ ${getFormattedTimeRemaining(order)}`}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Edit/Delete buttons for creator */}
            {canEdit && (
              <div className="flex gap-2">
                <button
                  onClick={handleEditClick}
                  disabled={isUpdating || isDeleting}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:bg-blue-500/30 transition-all disabled:opacity-50"
                >
                  <span>✏️</span>
                  <span className="hidden sm:inline">Sửa</span>
                </button>
                <button
                  onClick={handleDeleteOrder}
                  disabled={isUpdating || isDeleting}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-500/20 border border-red-500/30 text-red-300 hover:bg-red-500/30 transition-all disabled:opacity-50"
                >
                  {isDeleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-red-300 border-t-transparent rounded-full animate-spin"></div>
                      <span className="hidden sm:inline">Đang xóa...</span>
                    </>
                  ) : (
                    <>
                      <span>🗑️</span>
                      <span className="hidden sm:inline">Xóa</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Order Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/50">
              <span className="text-xl">📅</span>
              <div>
                <div className="text-xs text-slate-400">Ngày tạo</div>
                <div className="font-bold text-white">{order.date}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/50">
              <span className="text-xl">👥</span>
              <div>
                <div className="text-xs text-slate-400">Đã tham gia</div>
                <div className="font-bold text-white">{members.length} người</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/50">
              <span className="text-xl">⏰</span>
              <div>
                <div className="text-xs text-slate-400">Thời gian còn lại</div>
                <div className={`font-bold ${isOrderExpired(order) ? "text-red-400" : "text-white"}`}>
                  {getFormattedTimeRemaining(order)}
                </div>
              </div>
            </div>
          </div>

          {/* Link */}
          {order.link && (
            <div className="mb-6">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/50">
                <span className="text-xl">🔗</span>
                <div className="flex-1">
                  <div className="text-xs text-slate-400 mb-1">Link quán ăn</div>
                  <a
                    href={order.link.startsWith("http") ? order.link : `https://${order.link}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline font-medium text-sm break-all"
                  >
                    Xem menu & đặt hàng
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {order.notes && (
            <div className="mb-6">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50">
                <span className="text-xl">📝</span>
                <div className="flex-1">
                  <div className="text-xs text-slate-400 mb-1">Ghi chú</div>
                  <div className="text-sm text-slate-200 whitespace-pre-wrap">{order.notes}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* My Dish Input */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-600/50 rounded-2xl p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <span>🍽️</span>
            Món của bạn
          </h2>
          <div className="flex gap-3">
            <input
              ref={dishInputRef}
              type="text"
              defaultValue={userMember?.itemName || ''}
              placeholder="Ví dụ: Bún bò, Cơm gà, Trà sữa..."
              className="flex-1 px-4 py-3 rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:border-orange-500 transition-colors"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleUpdateDish();
                }
              }}
            />
            <button
              onClick={handleUpdateDish}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium transition-all"
            >
              Lưu
            </button>
          </div>
        </div>

        {/* Join/Leave Actions */}
        {order.status === 'open' && !isOrderExpired(order) && (
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-600/50 rounded-2xl p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">
                  {isUserMember ? 'Bạn đã tham gia order này' : 'Tham gia order'}
                </h3>
                <p className="text-slate-400">
                  {isUserMember ? 'Bạn có thể rời order hoặc cập nhật món ăn' : 'Nhập tên món và tham gia cùng mọi người'}
                </p>
              </div>
              
              {isUserMember ? (
                <button
                  onClick={handleLeaveOrder}
                  disabled={leavingOrder}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white font-bold transition-all disabled:opacity-50"
                >
                  {leavingOrder ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Đang rời...</span>
                    </>
                  ) : (
                    <>
                      <span>👋</span>
                      <span>Rời order</span>
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={() => {
                    const dish = dishInputRef.current?.value?.trim();
                    handleJoinOrder(dish || undefined);
                  }}
                  disabled={joiningOrder}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600 text-white font-bold transition-all disabled:opacity-50"
                >
                  {joiningOrder ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Đang tham gia...</span>
                    </>
                  ) : (
                    <>
                      <span>🎉</span>
                      <span>Tham gia ngay</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Members List */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-600/50 rounded-2xl p-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <span>👥</span>
            Danh sách tham gia ({members.length})
          </h2>
          
          {members.length > 0 ? (
            <div className="grid gap-3">
              {members.map((member) => (
                <div
                  key={member.id}
                  className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
                    member.userEmail === userEmail
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                      : 'bg-slate-700/50 border-slate-600/30 text-slate-300'
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 border border-blue-500/50 flex items-center justify-center">
                    <span className="text-lg">👤</span>
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">
                      {member.userName || member.userEmail}
                      {member.userEmail === userEmail && <span className="text-xs ml-2">(Bạn)</span>}
                    </div>
                    {member.itemName && (
                      <div className="text-sm text-slate-400 mt-1">
                        🍽️ {member.itemName}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <span className="text-4xl block mb-2">🤷‍♂️</span>
              Chưa có ai tham gia
            </div>
          )}
        </div>
      </div>

      {/* Edit Order Modal */}
      {isEditing && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 border border-slate-600 rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <span>✏️</span>
                Sửa Order
              </h3>
              <button
                onClick={() => setIsEditing(false)}
                disabled={isUpdating}
                className="text-slate-400 hover:text-white transition-colors disabled:cursor-not-allowed"
              >
                <span className="text-xl">❌</span>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Tên cửa hàng
                </label>
                <input
                  type="text"
                  value={editFormData.restaurant}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, restaurant: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="Tên cửa hàng..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Thời gian order (phút)
                </label>
                <input
                  type="number"
                  min="5"
                  max="480"
                  step="5"
                  value={editFormData.deadline}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, deadline: parseInt(e.target.value) || 60 }))}
                  className="w-full px-3 py-2 rounded-xl bg-slate-700 border border-slate-600 text-white focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Link quán ăn (tùy chọn)
                </label>
                <input
                  type="url"
                  value={editFormData.link}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, link: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="https://shopee.com/shop123 hoặc link menu..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Ghi chú (tùy chọn)
                </label>
                <textarea
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors resize-none"
                  placeholder="Thông tin về menu, giá cả, thanh toán..."
                  rows={3}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setIsEditing(false)}
                disabled={isUpdating}
                className="flex-1 px-4 py-2 rounded-xl bg-slate-600 text-slate-300 hover:bg-slate-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Hủy
              </button>
              <button
                onClick={handleUpdateOrder}
                disabled={isUpdating || !editFormData.restaurant.trim()}
                className="flex-1 px-4 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Đang cập nhật...
                  </div>
                ) : (
                  'Cập nhật'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Xóa"
        cancelText="Hủy"
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, isLoading: false })}
        isLoading={confirmModal.isLoading}
        type="danger"
      />
    </div>
  );
}
